import { useMemo } from 'react'

type Props = {
  value: string
  onChange: (value: string) => void
  className?: string
}

function parseBirthday(value: string) {
  if (!value) return { year: '', month: '', day: '' }
  const [year, month, day] = value.split('-')
  return { year: year || '', month: month ? String(Number(month)) : '', day: day ? String(Number(day)) : '' }
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export default function BirthdayInput({ value, onChange, className = 'oc-input' }: Props) {
  const { year, month, day } = parseBirthday(value)
  const today = new Date()
  const currentYear = today.getFullYear()

  const years = useMemo(() => {
    const result: number[] = []
    for (let y = currentYear; y >= 1900; y--) {
      result.push(y)
    }
    return result
  }, [currentYear])

  const currentMonth = today.getMonth() + 1
  const currentDay = today.getDate()

  const months = useMemo(() => {
    const max = Number(year) === currentYear ? currentMonth : 12
    return Array.from({ length: max }, (_, i) => i + 1)
  }, [year, currentYear, currentMonth])

  const days = useMemo(() => {
    const maxByMonth = year && month ? daysInMonth(Number(year), Number(month)) : 31
    const maxByToday = Number(year) === currentYear && Number(month) === currentMonth ? currentDay : maxByMonth
    return Array.from({ length: maxByToday }, (_, i) => i + 1)
  }, [year, month, currentYear, currentMonth, currentDay])

  const update = (part: 'year' | 'month' | 'day', val: string) => {
    let newYear = part === 'year' ? val : year
    let newMonth = part === 'month' ? val : month
    let newDay = part === 'day' ? val : day

    // 当年選択時に未来の月・日を補正
    if (Number(newYear) === currentYear) {
      if (Number(newMonth) > currentMonth) {
        newMonth = String(currentMonth)
      }
      if (Number(newMonth) === currentMonth && Number(newDay) > currentDay) {
        newDay = String(currentDay)
      }
    }

    // 日が月の最大日数を超える場合は補正
    if (newYear && newMonth && newDay) {
      const maxDay = daysInMonth(Number(newYear), Number(newMonth))
      if (Number(newDay) > maxDay) {
        newDay = String(maxDay)
      }
    }

    if (newYear && newMonth && newDay) {
      onChange(`${newYear}-${newMonth.padStart(2, '0')}-${newDay.padStart(2, '0')}`)
    } else if (!newYear && !newMonth && !newDay) {
      onChange('')
    } else {
      // 部分的に選択されている場合は空文字を維持しつつ、
      // 内部状態を保持するためにpartial valueを送る
      const y = newYear || '0000'
      const m = newMonth ? newMonth.padStart(2, '0') : '00'
      const d = newDay ? newDay.padStart(2, '0') : '00'
      onChange(`${y}-${m}-${d}`)
    }
  }

  const selectClass = `${className} appearance-none`

  return (
    <div className="flex items-center gap-2">
      <select className={selectClass} value={year} onChange={(e) => update('year', e.target.value)}>
        <option value="">年</option>
        {years.map((y) => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
      <span className="text-[#515862]">年</span>
      <select className={selectClass} value={month} onChange={(e) => update('month', e.target.value)}>
        <option value="">月</option>
        {months.map((m) => (
          <option key={m} value={String(m)}>{m}</option>
        ))}
      </select>
      <span className="text-[#515862]">月</span>
      <select className={selectClass} value={day} onChange={(e) => update('day', e.target.value)}>
        <option value="">日</option>
        {days.map((d) => (
          <option key={d} value={String(d)}>{d}</option>
        ))}
      </select>
      <span className="text-[#515862]">日</span>
    </div>
  )
}
